import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ShieldCheck } from "lucide-react";
import { TRUST_SIGNALS, STATUS_ITEMS, RISK_QA } from "@/data/compliance";
import { setPageMeta, injectJsonLd } from "@/lib/seo";
import { GoldButton } from "@/components/ds";
import { Spotlight } from "@/components/ds/Spotlight";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { Link } from "react-router-dom";

const StatusChip = ({ item }) => (
  <div data-testid={`status-${item.id}`} className="flex items-center gap-2.5 px-4 py-2.5 card-line bg-[#0A0A0B]">
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full rounded-full bg-[#C9A227] opacity-60 node-pulse" />
      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#D4AF37]" />
    </span>
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400">{item.label}</span>
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#E8C85A]">{item.state}</span>
  </div>
);

const TrustCard = ({ t, open, onToggle }) => (
  <div className={`card-line transition-colors ${open ? "bg-[#16161A]" : "bg-[#0A0A0B] hover:bg-[#121215]"}`}>
    <button
      type="button"
      data-testid={`trust-toggle-${t.id}`}
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
    >
      <span className="flex items-center gap-4">
        <ShieldCheck size={18} className={open ? "text-[#E8C85A]" : "text-[#C9A227]"} />
        <span>
          <span className={`font-display text-base tracking-wide block ${open ? "text-white" : "text-zinc-200"}`}>{t.label}</span>
          <span className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-400">{t.short}</span>
        </span>
      </span>
      {open ? <Minus size={16} className="text-[#C9A227] shrink-0" /> : <Plus size={16} className="text-zinc-400 shrink-0" />}
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="px-6 pb-6 pl-[62px] text-sm text-zinc-400 leading-relaxed" data-testid={`trust-detail-${t.id}`}>{t.detail}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Compliance = () => {
  const [openTrust, setOpenTrust] = useState("fatf");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "Compliance & Protection — Zorem Commodities LLC";
    setPageMeta({
      title: "Compliance & Protection | Zorem",
      description: "Zorem's working controls: FATF-aligned KYC/AML, OECD due diligence, OFAC·EU·UN·UK HMT sanctions screening, Dodd-Frank 1502, chain of custody, and independent assay.",
      path: "/compliance",
    });
    injectJsonLd("faq-jsonld", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: RISK_QA.map((x) => ({
        "@type": "Question",
        name: x.q,
        acceptedAnswer: { "@type": "Answer", text: x.a },
      })),
    });
    return () => document.getElementById("faq-jsonld")?.remove();
  }, []);

  return (
    <div data-testid="compliance-page" className="bg-[#0A0A0B] min-h-screen">
      <div className="pt-36 pb-16 bg-[#121215] hairline-b relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block">
          <img src="/assets/vault-sealed.webp" alt="Sealed pallets of gold bars inside a secure vault" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121215] via-[#121215]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <Eyebrow>Compliance & Protection</Eyebrow>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-wide text-white leading-tight">
              Trust is not a policy. <span className="gold-text">It is a procedure.</span>
            </h1>
            <p className="mt-6 text-base text-zinc-400 leading-relaxed">
              Every badge below is a working control — expand it to see exactly what it
              does, when it fires, and what happens when something fails it.
            </p>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="compliance-status">
              {STATUS_ITEMS.map((s) => <StatusChip key={s.id} item={s} />)}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <Eyebrow>Trust-Signal Grid</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white">Eight controls. Zero exceptions.</h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid="trust-grid">
          {TRUST_SIGNALS.map((t) => (
            <TrustCard key={t.id} t={t} open={openTrust === t.id} onToggle={() => setOpenTrust(openTrust === t.id ? null : t.id)} />
          ))}
        </div>

        <div className="mt-16 grid sm:grid-cols-3 gap-px bg-[#C9A227]/15 card-line" data-testid="compliance-gallery">
          <figure>
            <Spotlight>
              <img src="/assets/xrf-bench.webp" alt="Olympus benchtop XRF analyzer with stacked gold bars and precision scale" loading="lazy" className="w-full aspect-[4/5] object-cover hover:scale-[1.04] transition-transform duration-700" />
            </Spotlight>
            <figcaption className="px-4 py-3 bg-[#0A0A0B] font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-400">Assay devices · XRF + precision balance</figcaption>
          </figure>
          <figure>
            <Spotlight>
              <img src="/assets/dore-marked.webp" alt="Serialized doré bars marked for lot tracking" loading="lazy" className="w-full aspect-[4/5] object-cover hover:scale-[1.04] transition-transform duration-700" />
            </Spotlight>
            <figcaption className="px-4 py-3 bg-[#0A0A0B] font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-400">Marked doré lots · serialized at origin</figcaption>
          </figure>
          <figure>
            <Spotlight>
              <img src="/assets/vault-sealed.webp" alt="Strapped and sealed gold bar pallets in vault storage" loading="lazy" className="w-full aspect-[4/5] object-cover hover:scale-[1.04] transition-transform duration-700" />
            </Spotlight>
            <figcaption className="px-4 py-3 bg-[#0A0A0B] font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-400">Sealed vault inventory · documented custody</figcaption>
          </figure>
        </div>

        <div className="mt-24 grid lg:grid-cols-5 gap-12 items-start">
          <Reveal className="lg:col-span-2">
            <Eyebrow>Risk, In Plain Language</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white leading-snug">
              The questions every counterparty should ask.
            </h2>
            <p className="mt-5 text-sm text-zinc-400 leading-relaxed">
              No jargon, no hedging. If a counterparty anywhere in the market cannot
              answer these the way we do, that tells you something.
            </p>
          </Reveal>
          <div className="lg:col-span-3 space-y-3" data-testid="risk-accordion">
            {RISK_QA.map((item, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className={`card-line transition-colors ${open ? "bg-[#121215]" : "bg-[#0A0A0B] hover:bg-[#121215]/60"}`}>
                  <button
                    type="button"
                    data-testid={`risk-toggle-${i}`}
                    onClick={() => setOpenFaq(open ? null : i)}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className={`font-display text-base tracking-wide ${open ? "text-[#E8C85A]" : "text-zinc-200"}`}>{item.q}</span>
                    {open ? <Minus size={16} className="text-[#C9A227] shrink-0" /> : <Plus size={16} className="text-zinc-400 shrink-0" />}
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed" data-testid={`risk-answer-${i}`}>{item.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-24 card-line bg-[#121215] p-10 flex flex-wrap items-center justify-between gap-8">
          <div className="max-w-xl">
            <h3 className="font-display text-xl text-white tracking-wide">Read the full policy, or put it to the test.</h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              Our AML/KYC policy is public. Our due diligence is not theoretical —
              submit a mandate and watch it run.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <GoldButton to="/legal/aml" variant="ghost" testid="compliance-cta-policy">Read AML / KYC Policy</GoldButton>
            <GoldButton to="/mandate" testid="compliance-cta-mandate">Submit a Mandate</GoldButton>
          </div>
        </div>

        <p className="mt-8 text-center">
          <Link to="/process" data-testid="compliance-link-process" className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9A227] hover:text-[#E8C85A] transition-colors">
            See how compliance fits the five-step pathway →
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Compliance;
